import { Check, NotebookPen, Pencil, Trash2, X } from "lucide-react"
import { ComponentProps, useMemo, useState } from "react"
import { Button } from "../../atoms/Button"
import { Textarea } from "../../atoms/Textarea"
import { Card } from "../../molecules/Card"
import DateDisplay from "../../molecules/DateDisplay"
import { cn } from "../../utils"
import { ColorConfig } from "./Chat"
import { ChatMessage, ChatMessageProps, Message } from "./ChatMessage"
import { TypingIndicator } from "./TypingIndicator"

type AdditionalMessageOptions = Omit<
  ChatMessageProps,
  | keyof Message
  | "messages"
  | "messageNotGeneratedByPermutCopy"
  | "messageIsWhatsappTemplateCopy"
  | "messageIsAmphoraCopy"
  | "messageExternalSenderCopy"
  | "messageExternalDeletedCopy"
>

export type NoteItem = {
  id: string
  createdAt: string
  updatedAt: string
  text: string
  employee: { name: string }
  mentions?: Array<{ id: string; name: string }>
}

export type NoteComponentProps = {
  note: NoteItem
  onEditNote?: (id: string, text: string) => Promise<void>
  onDeleteNote?: (id: string) => void
}

interface MessageListProps {
  messages: Message[]
  noteItems?: NoteItem[]
  onEditNote?: (id: string, text: string) => Promise<void>
  onDeleteNote?: (id: string) => void
  noteComponent?: React.ComponentType<NoteComponentProps>
  isFakeCustomer?: boolean
  showTimeStamps?: boolean
  isTyping?: boolean
  typingLabel?: string
  messageOptions?: AdditionalMessageOptions | ((message: Message) => AdditionalMessageOptions)
  className?: string
  animation?: ComponentProps<typeof ChatMessage>["animation"]
  supportCopy: {
    questionCreated: string
    questionSolvedAutomatically: string
    ticketCreated: string
    questionSubjectLabel?: string
    questionSummaryLabel?: string
  }
  opportunityCopy?: string
  renderCapabilityExecutions?: (executions: Message["capabilityExecutions"]) => React.ReactNode
  renderCustomerLogMarkers?: ComponentProps<typeof ChatMessage>["renderCustomerLogMarkers"]
  colorConfig?: ColorConfig
  publicMode?: boolean
  replyMessageCopy?: (kind: Message["kind"]) => string
  messageNotGeneratedByPermutCopy: string
  messagePendingCopy?: string
  messagePendingTooltipCopy?: string
  messageIsWhatsappTemplateCopy: string
  messageIsAmphoraCopy: string
  messageExternalSenderCopy: string
  messageExternalDeletedCopy: string
  suppressLinks?: boolean
  hiddenLinkCopy?: string
  locale?: string
}

export function MessageList({
  messages,
  noteItems,
  onEditNote,
  onDeleteNote,
  noteComponent,
  isFakeCustomer,
  showTimeStamps = true,
  isTyping = false,
  typingLabel,
  messageOptions,
  className,
  animation,
  supportCopy,
  opportunityCopy,
  renderCapabilityExecutions,
  renderCustomerLogMarkers,
  colorConfig,
  publicMode = false,
  replyMessageCopy,
  messageNotGeneratedByPermutCopy,
  messagePendingCopy,
  messagePendingTooltipCopy,
  messageIsWhatsappTemplateCopy,
  messageIsAmphoraCopy,
  messageExternalSenderCopy,
  messageExternalDeletedCopy,
  suppressLinks,
  hiddenLinkCopy,
  locale,
}: MessageListProps) {
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editingText, setEditingText] = useState("")
  const [isSaving, setIsSaving] = useState(false)

  const sorted = useMemo(() => {
    const taggedMessages = messages.map((m) => ({
      type: "message" as const,
      item: m,
      sortKey: m.createdAt?.toISOString() ?? "",
    }))
    const taggedNotes = (noteItems ?? []).map((n) => ({
      type: "note" as const,
      item: n,
      sortKey: n.createdAt,
    }))
    return [...taggedMessages, ...taggedNotes].sort((a, b) => a.sortKey.localeCompare(b.sortKey))
  }, [messages, noteItems])

  const handleEditStart = (note: NoteItem) => {
    setEditingId(note.id)
    setEditingText(note.text)
  }

  const handleEditCancel = () => {
    setEditingId(null)
    setEditingText("")
  }

  const handleEditSave = async (id: string) => {
    if (!onEditNote) return
    setIsSaving(true)
    try {
      await onEditNote(id, editingText)
      setEditingId(null)
      setEditingText("")
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <div className={cn("space-y-4", className)}>
      {sorted.map((entry, index) => {
        if (entry.type === "note") {
          const note = entry.item

          if (noteComponent) {
            const NoteComp = noteComponent
            return (
              <NoteComp
                key={`note-${note.id}`}
                note={note}
                onEditNote={onEditNote}
                onDeleteNote={onDeleteNote}
              />
            )
          }

          const isEditing = editingId === note.id

          return (
            <Card
              key={`note-${note.id}`}
              id={`note-${note.id}`}
              className="p-0 rounded-lg duration-200 border border-warning/20 bg-warning/10 flex flex-col group/note"
            >
              <div className="flex items-center justify-between bg-warning/10 px-4 py-1">
                <div className="flex gap-2 text-foreground/70">
                  <NotebookPen size={16} />
                  <span className="text-xs font-medium">{note.employee.name}</span>
                </div>
                <div className="flex gap-2 text-xs text-muted-foreground">
                  {isEditing ? (
                    <div className="flex gap-1 justify-end">
                      <Button
                        type="button"
                        size="icon"
                        variant="outline"
                        onClick={handleEditCancel}
                        disabled={isSaving}
                        className="text-muted-foreground transition-colors !h-8 !w-8"
                      >
                        <X />
                      </Button>
                      <Button
                        type="button"
                        size="icon"
                        variant="outline"
                        onClick={() => handleEditSave(note.id)}
                        disabled={isSaving || editingText.trim().length === 0}
                        className="!text-blue-600 disabled:opacity-40 transition-colors !h-8 !w-8"
                      >
                        <Check className="mt-0.5" />
                      </Button>
                    </div>
                  ) : (
                    <div className="flex gap-1 opacity-0 duration-200 group-hover/note:opacity-100">
                      {onDeleteNote && (
                        <Button
                          type="button"
                          size="icon"
                          variant="outline"
                          onClick={() => onDeleteNote(note.id)}
                          className="text-muted-foreground hover:text-destructive transition-colors !w-8 !h-8"
                        >
                          <Trash2 />
                        </Button>
                      )}
                      {onEditNote && (
                        <Button
                          type="button"
                          size="icon"
                          variant="outline"
                          onClick={() => handleEditStart(note)}
                          className="text-muted-foreground hover:text-foreground transition-colors !w-8 !h-8"
                        >
                          <Pencil />
                        </Button>
                      )}
                    </div>
                  )}
                  <DateDisplay date={note.createdAt} />
                </div>
              </div>
              <div className="flex gap-2 px-4 py-4 items-center">
                <div className="w-full">
                  {isEditing ? (
                    <div className="flex flex-col gap-2">
                      <Textarea
                        value={editingText}
                        onChange={(e) => setEditingText(e.target.value)}
                        className="text-sm text-foreground"
                        rows={3}
                        autoFocus
                      />
                    </div>
                  ) : (
                    <p className="text-sm text-foreground whitespace-pre-wrap italic">
                      {note.text}
                    </p>
                  )}
                </div>
              </div>
            </Card>
          )
        }

        const message = entry.item
        let additionalOptions: AdditionalMessageOptions | undefined
        if (!message.externalDeletedAt) {
          additionalOptions =
            typeof messageOptions === "function" ? messageOptions(message) : messageOptions
        }

        return (
          <ChatMessage
            key={index}
            isFakeCustomer={isFakeCustomer}
            showTimeStamp={showTimeStamps}
            animation={animation}
            supportCopy={supportCopy}
            opportunityCopy={opportunityCopy}
            renderCapabilityExecutions={renderCapabilityExecutions}
            renderCustomerLogMarkers={renderCustomerLogMarkers}
            colorConfig={colorConfig}
            publicMode={publicMode}
            messages={messages}
            replyMessageCopy={replyMessageCopy}
            messageNotGeneratedByPermutCopy={messageNotGeneratedByPermutCopy}
            messagePendingCopy={messagePendingCopy}
            messagePendingTooltipCopy={messagePendingTooltipCopy}
            messageIsWhatsappTemplateCopy={messageIsWhatsappTemplateCopy}
            messageIsAmphoraCopy={messageIsAmphoraCopy}
            messageExternalSenderCopy={messageExternalSenderCopy}
            messageExternalDeletedCopy={messageExternalDeletedCopy}
            suppressLinks={suppressLinks}
            hiddenLinkCopy={hiddenLinkCopy}
            locale={locale}
            {...message}
            {...additionalOptions}
          />
        )
      })}
      {isTyping && <TypingIndicator colorConfig={colorConfig} label={typingLabel} />}
    </div>
  )
}
